import { useCallback, useEffect, useMemo, useState } from "react";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import Stack from "@mui/material/Stack";

import MDButton from "components/MDButton";
import { LojaPageContainer } from "layouts/pages/loja/common";
import { getMonthRange, getTodayRange, getWeekRange } from "helpers/rangerDate";
import { formatDate, formatDateEndOfDay } from "helpers/formatDate";
import { TypeStatusInscricao } from "interfaces/inscricoes";
import {
  IRelPaymentsPeriod,
  periodQueryType,
  RelatorioPaymentsTipoData,
} from "interfaces/reports";
import { getPaymentsPeriod } from "services/reports";
import generatePDF from "helpers/generatePDF";
import { getImageBase64 } from "helpers/getImageBase64";
import logoTNV from "assets/images/logo-tnv.png";
import { formatCurrency } from "helpers/utils";
import { exportToExcel } from "helpers/exportFileToExcel";
import type { TableCell } from "pdfmake/interfaces";

import TabsVisaoGeral from "../componets/TabsVisaoGeral";
import ExtratoResumoCards from "./components/ExtratoResumoCards";
import ExtratoFiltros, { type PeriodPreset } from "./components/ExtratoFiltros";
import { EXTRATO_ACCENT } from "./components/extrato.theme";

const TIPO_DATA_LABEL: Record<RelatorioPaymentsTipoData, string> = {
  pagamento: "data de pagamento / vencimento",
  inscricao: "data de inscrição",
};

const PERIOD_RANGES = {
  day: getTodayRange(),
  week: getWeekRange(),
  month: getMonthRange(),
};

export default function VisaoGeral() {
  const dayRange = useMemo(() => PERIOD_RANGES.day, []);

  const [periodPreset, setPeriodPreset] = useState<PeriodPreset>("day");
  const [tipoData, setTipoData] = useState<RelatorioPaymentsTipoData>("pagamento");
  const [dataInicio, setDataInicio] = useState(dayRange.startDate);
  const [dataFim, setDataFim] = useState(dayRange.endDate);
  const [periodQuery, setPeriodQuery] = useState<periodQueryType>();
  const [reports, setReports] = useState<IRelPaymentsPeriod[]>([]);
  const [totals, setTotals] = useState<
    Partial<{ [k in TypeStatusInscricao]: { qtd: number; total: number } }>
  >({});
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState<"pdf" | "excel" | null>(null);

  const handlerPaymentsPeriod = useCallback(
    async (
      startDate: string,
      endDate: string,
      filtroTipoData: RelatorioPaymentsTipoData = tipoData
    ) => {
      setLoading(true);
      try {
        const { totals: nextTotals, data, status } = await getPaymentsPeriod(
          startDate,
          endDate,
          filtroTipoData
        );

        if (status === 200) {
          setReports(data);
          setTotals(nextTotals);
          setPeriodQuery({ startDate, endDate, tipoData: filtroTipoData });
          setDataInicio(startDate);
          setDataFim(endDate);
        }
      } finally {
        setLoading(false);
      }
    },
    [tipoData]
  );

  useEffect(() => {
    void handlerPaymentsPeriod(dayRange.startDate, dayRange.endDate, tipoData);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- carga inicial do dia
  }, []);

  const handlePeriodPresetChange = (preset: "day" | "week" | "month") => {
    setPeriodPreset(preset);
    const range = PERIOD_RANGES[preset];
    void handlerPaymentsPeriod(range.startDate, range.endDate, tipoData);
  };

  const handleAplicarPeriodo = () => {
    if (!dataInicio || !dataFim) return;
    setPeriodPreset("custom");
    void handlerPaymentsPeriod(dataInicio, dataFim, tipoData);
  };

  const handleTipoDataChange = (next: RelatorioPaymentsTipoData) => {
    setTipoData(next);
    if (dataInicio && dataFim) {
      void handlerPaymentsPeriod(dataInicio, dataFim, next);
    }
  };

  const periodoLabel = periodQuery
    ? `${formatDateEndOfDay(periodQuery.startDate)} a ${formatDateEndOfDay(periodQuery.endDate)} · ${TIPO_DATA_LABEL[periodQuery.tipoData ?? tipoData]}`
    : "Selecione um período";

  const handlerGeneratePDF = async () => {
    if (!periodQuery) return;
    setExporting("pdf");
    try {
      const base64Image = await getImageBase64(logoTNV);
      await generatePDF({
        filename: `Extrato financeiro - ${formatDateEndOfDay(
          periodQuery.startDate,
          "yyyy-MM-dd"
        )}-${formatDateEndOfDay(periodQuery.endDate, "yyyy-MM-dd")}.pdf`,
        header: {
          text: "EXTRATO FINANCEIRO",
          alignment: "center",
          margin: [40, 40, 40, 40],
          bold: true,
          fontSize: 16,
        },
        content: [
          {
            layout: "noBorders",
            margin: [0, 10, 0, 10],
            table: {
              widths: ["auto", "*"],
              body: [
                [
                  { image: base64Image, fit: [80, 80] },
                  {
                    text: [
                      {
                        text: `Período:\n ${formatDateEndOfDay(
                          periodQuery.startDate,
                          "dd/MM/yyyy"
                        )} - ${formatDateEndOfDay(periodQuery.endDate, "dd/MM/yyyy")}`,
                        fontSize: 12,
                      },
                    ],
                  },
                ],
              ],
            },
          },
          {
            fontSize: 9,
            table: {
              widths: ["*", "*", "auto", "auto", "auto", "auto", "auto", "*", "auto"],
              body: [
                [
                  { text: "Dt. Pagamento", alignment: "center", bold: true, fillColor: "#CCCCCC" },
                  { text: "N.Inscrição", alignment: "center", bold: true, fillColor: "#CCCCCC" },
                  { text: "Taxa", alignment: "right", bold: true, fillColor: "#CCCCCC" },
                  { text: "T.Taxa", alignment: "right", bold: true, fillColor: "#CCCCCC" },
                  { text: "Desconto", alignment: "right", bold: true, fillColor: "#CCCCCC" },
                  { text: "Sub Total", alignment: "right", bold: true, fillColor: "#CCCCCC" },
                  { text: "Total", alignment: "right", bold: true, fillColor: "#CCCCCC" },
                  { text: "Tipo Pag.", bold: true, fillColor: "#CCCCCC", alignment: "center" },
                  { text: "Status", bold: true, fillColor: "#CCCCCC", alignment: "center" },
                ],
                ...reports.map(
                  (report): TableCell[] => [
                    {
                      text: formatDateEndOfDay(report.dataBaixa, "dd/MM/yyyy"),
                      alignment: "center" as const,
                    },
                    { text: `${report.inscricaoEvento.numeroInscricao}`, alignment: "center" as const },
                    { text: formatCurrency(report.inscricaoEvento.taxa), alignment: "right" as const },
                    { text: formatCurrency(report.inscricaoEvento.totalTaxa), alignment: "right" as const },
                    { text: formatCurrency(report.inscricaoEvento.totalDesconto), alignment: "right" as const },
                    { text: formatCurrency(report.inscricaoEvento.subTotal), alignment: "right" as const },
                    { text: formatCurrency(report.inscricaoEvento.totalInscricao), alignment: "right" as const },
                    {
                      text: report.cobrancaPix
                        ? "PIX"
                        : report.cobrancaBoleto?.card_mask
                          ? `CARTAO-(${report.cobrancaBoleto.installments}X)`
                          : "BOLETO",
                      alignment: "center" as const,
                    },
                    { text: `${report.inscricaoEvento.statusInscricao.tipo}`, alignment: "center" as const },
                  ]
                ),
              ] as TableCell[][],
            },
          },
          { text: "\nTOTAIS\n", bold: true, fontSize: 14 },
          { text: `Pagamentos confirmados...: ${formatCurrency(totals?.Pago?.total || 0)}\n`, fontSize: 11 },
          { text: `Pagamentos pendentes.....: ${formatCurrency(totals?.Pendente?.total || 0)}\n`, fontSize: 11 },
          { text: `Pagamentos Cancelados....: ${formatCurrency(totals?.Cancelado?.total || 0)}\n`, fontSize: 11 },
        ],
        footer: {
          columns: [
            { text: "https://www.tnvteam.com.br/", alignment: "left", margin: [40, 0, 0, 0], fontSize: 9 },
            {
              text: formatDate(new Date(), "dd/MM/yyyy HH:mm:ss"),
              alignment: "right",
              margin: [0, 40, 40, 0],
              fontSize: 9,
            },
          ],
        },
      });
    } finally {
      setExporting(null);
    }
  };

  const handleGenerateExcel = async () => {
    if (!periodQuery || reports.length === 0) return;
    setExporting("excel");
    try {
      const dataExcel = reports.map((report) => ({
        "Dt. Pagamento": formatDateEndOfDay(report.dataBaixa, "dd/MM/yyyy"),
        "N.Inscrição": report.inscricaoEvento.numeroInscricao,
        Taxa: Number(report.inscricaoEvento.taxa),
        "T.Taxa": Number(report.inscricaoEvento.totalTaxa),
        Desconto: Number(report.inscricaoEvento.totalDesconto),
        "Sub Total": Number(report.inscricaoEvento.subTotal),
        "Qtd Parcelas": Number(
          report.cobrancaPix
            ? 1
            : report.cobrancaBoleto.card_mask
              ? report.cobrancaBoleto.installments
              : 1
        ),
        Total: Number(report.inscricaoEvento.totalInscricao),
        "Tipo Pag.": report.cobrancaPix
          ? "PIX"
          : report.cobrancaBoleto.card_mask
            ? "CARTAO"
            : "BOLETO",
        Status: report.inscricaoEvento.statusInscricao.tipo,
      }));

      const header = Object.keys(dataExcel[0]);
      exportToExcel({
        title: `Relatório dos recebimentos - ${periodQuery.startDate}-${periodQuery.endDate}`,
        headerItem: header,
        rowsTable: dataExcel,
        nameWorkbook: "Extrato",
        filename: `Extrato - ${periodQuery.startDate}-${periodQuery.endDate}`,
        options: [
          {
            title: "Total",
            data: [
              { description: "Confirmados", value: totals?.Pago?.total },
              { description: "Pendentes", value: totals?.Pendente?.total },
              { description: "Cancelados", value: totals?.Cancelado?.total },
            ],
          },
        ],
      });
    } finally {
      setExporting(null);
    }
  };

  return (
    <LojaPageContainer
      title="Extrato de inscrições"
      subtitle="Recebimentos de inscrições em eventos — confirmadas, pendentes e canceladas."
      actions={
        <Stack direction="row" spacing={1}>
          <MDButton
            variant="outlined"
            color="dark"
            size="small"
            startIcon={<TableChartOutlinedIcon />}
            disabled={loading || reports.length === 0 || exporting !== null}
            onClick={() => void handleGenerateExcel()}
          >
            {exporting === "excel" ? "Exportando…" : "Excel"}
          </MDButton>
          <MDButton
            variant="gradient"
            color="dark"
            size="small"
            startIcon={<PictureAsPdfIcon />}
            disabled={loading || !periodQuery || exporting !== null}
            onClick={() => void handlerGeneratePDF()}
            sx={{
              background: `linear-gradient(195deg, ${EXTRATO_ACCENT}, #e04a1a)`,
            }}
          >
            {exporting === "pdf" ? "Gerando…" : "PDF"}
          </MDButton>
        </Stack>
      }
    >
      <ExtratoResumoCards totals={totals} totalRegistros={reports.length} />

      <ExtratoFiltros
        periodPreset={periodPreset}
        periodoLabel={periodoLabel}
        dataInicio={dataInicio}
        dataFim={dataFim}
        tipoData={tipoData}
        loading={loading}
        onPeriodPresetChange={handlePeriodPresetChange}
        onDataInicioChange={setDataInicio}
        onDataFimChange={setDataFim}
        onAplicarPeriodo={handleAplicarPeriodo}
        onTipoDataChange={handleTipoDataChange}
      />

      <TabsVisaoGeral data={reports} totals={totals} loading={loading} />
    </LojaPageContainer>
  );
}
